import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { safeNextPath } from '../utils/safePath';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ 
    type: 'success' | 'error'; 
    text: string;
    showSignupLink?: boolean;
    showResetPasswordLink?: boolean;
  } | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { signIn, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));

  useEffect(() => {
    if (user) {
      navigate(nextPath, { replace: true });
    }
  }, [user, navigate, nextPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setEmailError(null);
    setPasswordError(null);
    setIsSubmitting(true);

    // Basic validation
    if (!email.trim()) {
      setEmailError('Email is required');
      setIsSubmitting(false);
      return;
    }

    if (!password.trim()) {
      setPasswordError('Password is required');
      setIsSubmitting(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await signIn(email, password);
      
      // Show loading screen after successful authentication
      setIsInitialLoading(true);
      setProgress(0);
      
      // Animate progress bar from 0 to 100 over 2 seconds
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 2; // Increment by 2% every 40ms
        });
      }, 40);
      
      setTimeout(() => {
        setIsInitialLoading(false);
        navigate(nextPath, { replace: true });
      }, 2000);
      
    } catch (error: any) {
      // Extract error message - handle all possible error structures
      const errorMessage = error?.message || error?.error?.message || String(error || 'Unknown error');
      const errorCode = error?.code || error?.error?.code || '';
      
      // Default message
      let userMessage = 'Invalid email or password. If you don\'t have an account, please sign up first.';
      let showSignupLink = true;
      
      // Check for specific error types
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('invalid login') || errorLower.includes('invalid email or password')) {
        userMessage = 'Invalid email or password. If you don\'t have an account, please sign up first.';
        showSignupLink = true;
      } else if (errorLower.includes('email not confirmed') || errorLower.includes('not verified')) {
        userMessage = 'Please check your email and click the confirmation link to activate your account.';
        showSignupLink = false;
      } else if (errorLower.includes('too many')) {
        userMessage = 'Too many sign-in attempts. Please wait a few minutes and try again.';
        showSignupLink = false;
      } else if (errorMessage && errorMessage !== 'Unknown error') {
        userMessage = errorMessage;
        showSignupLink = true;
      }
      
      // IMMEDIATELY set the error message - no delays, no conditions
      setMessage({
        type: 'error',
        text: userMessage,
        showSignupLink,
        showResetPasswordLink: false
      });
      
      setEmailError('Invalid credentials');
      setPasswordError('Invalid credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 rounded-2xl p-12 border border-gray-700 shadow-lg">
            <div className="flex flex-col items-center justify-center space-y-6">
              {/* Loading Spinner */}
              <div className="relative">
                <div className="w-16 h-16 border-4 border-gray-600 border-t-cashy-purple-500 rounded-full animate-spin"></div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-64 bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-cashy-purple-500 to-cashy-purple-400 h-2 rounded-full transition-all duration-75 ease-out" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              
              {/* Loading Text */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-white mb-2">Signing You In</h3>
                <p className="text-gray-400 text-sm">Preparing your dashboard...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cashy-purple-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-8 h-8 flex items-end justify-center gap-1 px-1 flex-shrink-0">
              {/* Three bars getting bigger from left to right */}
              <div className="w-1.5 bg-cashy-purple-400 rounded-sm" style={{ height: '14px' }}></div>
              <div className="w-1.5 bg-cashy-purple-500 rounded-sm" style={{ height: '18px' }}></div>
              <div className="w-1.5 bg-cashy-purple-600 rounded-sm" style={{ height: '22px' }}></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">cashy.so</h1>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-2 text-gray-600">Sign in to your account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error/Success Message - Always render if message exists */}
          {message && message.text && (
            <div 
              className={`p-5 rounded-lg flex items-start gap-3 mb-4 animate-fade-in ${
                message.type === 'error' 
                  ? 'bg-red-100 border-2 border-red-600 text-red-800 shadow-lg shadow-red-500/30' 
                  : 'bg-green-100 border-2 border-green-600 text-green-800 shadow-lg shadow-green-500/30'
              }`} 
              role="alert"
              aria-live="assertive"
              style={{ display: 'block' }}
            >
              <AlertCircle size={24} className={`mt-0.5 flex-shrink-0 ${message.type === 'error' ? 'text-red-700' : 'text-green-700'}`} />
              <div className="flex-1">
                <p className="font-bold text-lg leading-relaxed mb-2">{message.text}</p>
                {message.type === 'error' && message.showSignupLink && (
                  <p className="text-sm mt-2">
                    <Link to="/signup" className="underline hover:no-underline font-bold text-red-700 hover:text-red-800 transition-colors">
                      Don't have an account? Sign up here →
                    </Link>
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                  if (message) setMessage(null);
                }}
                onBlur={(e) => {
                  if (!e.target.value.trim()) {
                    setEmailError('Email is required');
                  } else {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(e.target.value)) {
                      setEmailError('Please enter a valid email address');
                    } else {
                      setEmailError(null);
                    }
                  }
                }}
                className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-500 ${
                  emailError 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-cashy-purple-500 focus:border-transparent'
                }`}
                placeholder="Enter your email"
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {emailError}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                    if (message) setMessage(null);
                  }}
                  onBlur={(e) => {
                    if (!e.target.value.trim()) {
                      setPasswordError('Password is required');
                    } else {
                      setPasswordError(null);
                    }
                  }}
                  className={`w-full px-4 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-500 pr-12 ${
                    passwordError 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-cashy-purple-500 focus:border-transparent'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {passwordError}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            className={`w-full text-white font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
              isLoading || isSubmitting
                ? 'bg-cashy-purple-400 cursor-not-allowed'
                : 'bg-cashy-purple-500 hover:bg-cashy-purple-600 hover:shadow-lg hover:shadow-cashy-purple-500/30 active:scale-95'
            }`}
          >
            {isLoading || isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogIn size={20} />
                Sign In
              </>
            )}
          </button>

          <div className="text-center space-y-2">
            <p>
              <Link to="/forgot-password" className="text-cashy-purple-500 hover:text-cashy-purple-600 transition-colors">
                Forgot password?
              </Link>
            </p>
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-cashy-purple-500 hover:text-cashy-purple-600 transition-colors">
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};